import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, ShieldCheck, Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { checkOriginality, type OriginalityReport } from "@/lib/originality.functions";

export const Route = createFileRoute("/originality")({
  component: OriginalityPage,
  head: () => ({
    meta: [
      { title: "Originality Checker - Inquira" },
      {
        name: "description",
        content:
          "Check your own writing for passages that read as copied or AI-generated, with rewriting and citation tips.",
      },
      { property: "og:title", content: "Originality Checker - Inquira" },
      {
        property: "og:description",
        content: "Paste your draft and see which passages read as copied source material or machine-written.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/originality" }],
  }),
});

const MAX = 12000;

function tone(score: number) {
  if (score >= 75) return "text-green-600";
  if (score >= 45) return "text-amber-600";
  return "text-destructive";
}

function OriginalityPage() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState<OriginalityReport | null>(null);

  useEffect(() => {
    if (!isLoading && !user) navigate({ to: "/login" });
  }, [isLoading, user, navigate]);

  async function run() {
    const trimmed = text.trim();
    if (trimmed.length < 50) {
      toast.error("Paste at least 50 characters");
      return;
    }
    setRunning(true);
    setReport(null);
    try {
      setReport(await checkOriginality({ data: { text: trimmed } }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Originality check failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <Link to="/" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>

      <header className="mb-4">
        <h1 className="flex items-center gap-2 text-2xl font-extrabold">
          <ShieldCheck className="h-6 w-6 text-primary" />
          Originality checker
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Paste your own draft. Inquira flags passages that read as copied source material or
          machine-generated and suggests how to rewrite or cite them.
        </p>
      </header>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, MAX))}
        rows={10}
        placeholder="Paste your essay, summary or answer here…"
        className="w-full rounded-xl border border-border bg-background p-3 text-sm"
      />
      <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
        <span>Minimum 50 characters · your text is never shared with other users</span>
        <span>
          {text.trim().length}/{MAX}
        </span>
      </div>

      <Button onClick={run} disabled={running} className="mt-3 w-full">
        <Sparkles className="mr-2 h-4 w-4" />
        {running ? "Checking…" : "Check originality"}
      </Button>

      {report && (
        <section className="mt-6 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl border border-border p-3 text-center">
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Originality
              </div>
              <div className={`text-2xl font-extrabold ${tone(report.originalityScore)}`}>
                {report.originalityScore}
              </div>
            </div>
            <div className="rounded-xl border border-border p-3 text-center">
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                AI-written
              </div>
              <div className="text-2xl font-extrabold">{report.aiLikelihood}%</div>
            </div>
            <div className="rounded-xl border border-border p-3 text-center">
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Copied
              </div>
              <div className="text-2xl font-extrabold">{report.copiedLikelihood}%</div>
            </div>
          </div>

          <div className="rounded-xl border border-border p-3">
            <div className="text-sm font-semibold">{report.verdict}</div>
            <p className="mt-1 text-sm text-muted-foreground">{report.summary}</p>
            <p className="mt-2 text-xs text-muted-foreground">
              {report.wordCount} words · checked {new Date(report.checkedAt).toLocaleString()}
            </p>
          </div>

          {report.flags.length > 0 && (
            <div>
              <h2 className="mb-2 text-sm font-semibold">Flagged passages</h2>
              <ul className="space-y-2">
                {report.flags.map((f, i) => (
                  <li key={i} className="rounded-xl border border-border p-3 text-sm">
                    <div className="mb-1 flex items-center gap-2 text-xs">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                      <span className="capitalize">{f.kind === "ai" ? "AI-like" : f.kind}</span>
                      <span className="text-muted-foreground">· {f.confidence}% confidence</span>
                    </div>
                    <blockquote className="border-l-2 border-amber-500/60 pl-2 italic">
                      {f.excerpt}
                    </blockquote>
                    <p className="mt-1 text-xs text-muted-foreground">{f.reason}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.advice.length > 0 && (
            <div className="rounded-xl border border-border p-3">
              <h2 className="mb-1 text-sm font-semibold">How to improve it</h2>
              <ul className="list-disc pl-5 text-sm text-muted-foreground">
                {report.advice.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            This is a signals-based originality estimate, not a database match — always double-check
            before relying on it for academic decisions.
          </p>
        </section>
      )}
    </div>
  );
}
